import { UtilFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { DateHelper } from "utils/helpers";

// toJSON() serialises in UTC, which turns a local month start in CET/CEST into the previous month.
const toYearMonthString = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const QueryStateHelper = {
  stringQueryEncoder(name: string) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: string, newValue?: string) {
        if (newValue != null && newValue !== currentValue) {
          params.set(name, encodeURIComponent(newValue));
        } else if (newValue == null) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const value = params.get(name);
        if (value != null) {
          return decodeURIComponent(value);
        }
      },
    };
  },
  booleanQueryEncoder(name: string) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: boolean, newValue?: boolean) {
        if (newValue != null && newValue !== currentValue) {
          params.set(name, encodeURIComponent(newValue));
        } else if (newValue == null) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const valueString = params.get(name);
        if (valueString != null) {
          return Boolean(valueString === "true");
        }
      },
    };
  },
  numberQueryEncoder(name: string, skipZeros?: boolean) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: number, newValue?: number) {
        if (
          newValue != null &&
          (newValue as any) !== "" &&
          newValue !== currentValue &&
          (!skipZeros || newValue !== 0)
        ) {
          params.set(name, encodeURIComponent(newValue));
        } else if (newValue == null || (newValue as any) === "" || (skipZeros && newValue === 0)) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const valueString = params.get(name);
        if (valueString != null) {
          return Number(valueString);
        }
      },
    };
  },
  numberRangeQueryEncoder(name: string) {
    const start = name + "Start";
    const end = name + "End";
    return {
      urlEncode(
        params: URLSearchParams,
        currentValue?: UtilFormTypes.NumberRange,
        newValue?: UtilFormTypes.NumberRange
      ) {
        const currentHighest = currentValue?.highest;
        const currentLowest = currentValue?.lowest;
        const newHighest = newValue?.highest;
        const newLowest = newValue?.lowest;
        if (newLowest != null && (newLowest as any) !== "" && newLowest !== currentLowest) {
          params.set(start, encodeURIComponent(newLowest));
        } else if ((newLowest == null || (newLowest as any) === "") && currentLowest != null) {
          params.delete(start);
        }
        if (newHighest != null && (newHighest as any) !== "" && newHighest !== currentHighest) {
          params.set(end, encodeURIComponent(newHighest));
        } else if ((newHighest == null || (newHighest as any) === "") && currentHighest != null) {
          params.delete(end);
        }
      },
      urlDecode(params: URLSearchParams): UtilFormTypes.NumberRange | undefined {
        const highest = params.get(end);
        const lowest = params.get(start);
        return {
          highest: highest == null ? undefined : Number(highest),
          lowest: lowest == null ? undefined : Number(lowest),
        };
      },
    };
  },
  dateQueryEncoder(name: string) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: Date, newValue?: Date) {
        if (newValue != null && newValue !== currentValue) {
          params.set(name, encodeURIComponent(newValue.toJSON().slice(0, 10)));
        } else if (newValue == null && currentValue != null) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const dateString = params.get(name);
        if (dateString != null) {
          return DateHelper.resetTime(new Date(dateString));
        }
      },
    };
  },
  dateRangeQueryEncoder(name: string) {
    const start = name + "Start";
    const end = name + "End";

    return {
      urlEncode(params: URLSearchParams, currentValue?: UtilFormTypes.DateRange, newValue?: UtilFormTypes.DateRange) {
        const currentStart = currentValue?.start;
        const currentEnd = currentValue?.end;
        const newStart = newValue?.start;
        const newEnd = newValue?.end;
        if (newStart != null && newStart !== currentStart) {
          params.set(start, encodeURIComponent(encodeURIComponent(newStart.toJSON().slice(0, 10))));
        } else if (newStart == null && currentStart != null) {
          params.delete(start);
        }
        if (newEnd != null && newEnd !== currentEnd) {
          params.set(end, encodeURIComponent(encodeURIComponent(newEnd.toJSON().slice(0, 10))));
        } else if (newEnd == null && currentEnd != null) {
          params.delete(end);
        }
      },
      urlDecode(params: URLSearchParams) {
        const startDateString = params.get(start);
        const endDateString = params.get(end);
        return {
          start: DateHelper.resetTime(startDateString == null ? undefined : new Date(startDateString)),
          end: DateHelper.resetTime(endDateString == null ? undefined : new Date(endDateString)),
        };
      },
    };
  },
  monthQueryEncoder(name: string) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: Date, newValue?: Date) {
        if (newValue != null && newValue !== currentValue) {
          params.set(name, encodeURIComponent(newValue.toJSON().slice(0, 7)));
        } else if (newValue == null && currentValue != null) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const monthString = params.get(name);
        if (monthString != null) {
          return DateHelper.resetTime(new Date(monthString));
        }
      },
    };
  },
  monthRangeQueryEncoder(name: string) {
    const start = name + "Start";
    const end = name + "End";

    return {
      urlEncode(params: URLSearchParams, currentValue?: UtilFormTypes.DateRange, newValue?: UtilFormTypes.DateRange) {
        const currentStart = currentValue?.start;
        const currentEnd = currentValue?.end;
        const newStart = newValue?.start;
        const newEnd = newValue?.end;
        if (newStart != null && newStart !== currentStart) {
          params.set(start, encodeURIComponent(encodeURIComponent(toYearMonthString(newStart))));
        } else if (newStart == null && currentStart != null) {
          params.delete(start);
        }
        if (newEnd != null && newEnd !== currentEnd) {
          params.set(end, encodeURIComponent(encodeURIComponent(toYearMonthString(newEnd))));
        } else if (newEnd == null && currentEnd != null) {
          params.delete(end);
        }
      },
      urlDecode(params: URLSearchParams) {
        const startDateString = params.get(start);
        const endDateString = params.get(end);
        return {
          start: DateHelper.resetTime(startDateString == null ? undefined : new Date(startDateString)),
          end: DateHelper.resetTime(
            endDateString == null ? undefined : DateHelper.getLastDayOfMonth(new Date(endDateString))
          ),
        };
      },
    };
  },
  selectionItemsQueryEncoder(name: string, items: SelectionItem[]) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: SelectionItem[], newValue?: SelectionItem[]) {
        if (newValue != null && newValue.length !== 0) {
          params.set(name, encodeURIComponent(newValue.map((v) => v.id).join(",")));
        } else if ((newValue == null || newValue.length === 0) && currentValue != null && currentValue.length !== 0) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const valueString = params.get(name);
        if (valueString == null) {
          return undefined;
        }
        const result = decodeURIComponent(valueString)
          .split(",")
          .reduce((groupedIds: SelectionItem[], id) => {
            const correspondingItem = items.find((v) =>
              typeof v.id === "number" ? v.id === Number(id) : v.id.split(",").includes(id)
            );
            const existingEntry = groupedIds.find((v) => v.id === correspondingItem?.id);
            if (correspondingItem == null || existingEntry) return groupedIds;
            groupedIds.push(correspondingItem);
            return groupedIds;
          }, [] as SelectionItem[]);

        return result;
      },
    };
  },

  multipleAutoCompleteItemsQueryEncoder(name: string) {
    const idField = `${name}Ids`;
    const nameField = `${name}Names`;
    return {
      urlEncode(params: URLSearchParams, currentValue?: AutocompleteListItem[], newValue?: AutocompleteListItem[]) {
        if (newValue != null && newValue.length !== 0) {
          params.set(idField, encodeURIComponent(newValue.map((v) => v.id).join(",")));
          params.set(nameField, encodeURIComponent(newValue.map((v) => v.value).join(",")));
        } else if ((newValue == null || newValue.length === 0) && currentValue != null && currentValue.length !== 0) {
          params.delete(idField);
          params.delete(nameField);
        }
      },
      urlDecode(params: URLSearchParams) {
        const idValueString = params.get(idField);
        const nameValueString = params.get(nameField);

        if (idValueString == null || nameValueString == null) {
          return undefined;
        }

        const ids = decodeURIComponent(idValueString).split(",");
        const names = decodeURIComponent(nameValueString).split(",");
        return ids.map((id, index) => ({ id: id, value: names[index] }));
      },
    };
  },

  dimensionsQueryEncoder(name: string) {
    return {
      urlEncode(params: URLSearchParams, currentValue?: string[], newValue?: string[]) {
        if (newValue != null && newValue.length !== 0) {
          params.set(name, encodeURIComponent(newValue.join(",")));
        } else if ((newValue == null || newValue.length === 0) && currentValue != null && currentValue.length !== 0) {
          params.delete(name);
        }
      },
      urlDecode(params: URLSearchParams) {
        const valueString = params.get(name);
        if (valueString == null) {
          return undefined;
        }
        return decodeURIComponent(valueString).split(",");
      },
    };
  },

  monthRangeListQueryEncoder(name: string) {
    const starts = name + "Start";
    const ends = name + "End";
    return {
      urlEncode(
        params: URLSearchParams,
        currentValue?: UtilFormTypes.DateRange[],
        newValue?: UtilFormTypes.DateRange[]
      ) {
        // An empty list is written as empty params rather than removed, so that "no comparison columns"
        // survives a reload instead of falling back to the default column.
        if (newValue != null) {
          params.set(
            starts,
            encodeURIComponent(newValue.map((v) => (v.start ? toYearMonthString(v.start) : "")).join(","))
          );
          params.set(ends, encodeURIComponent(newValue.map((v) => (v.end ? toYearMonthString(v.end) : "")).join(",")));
        } else if (currentValue != null) {
          params.delete(starts);
          params.delete(ends);
        }
      },
      urlDecode(params: URLSearchParams): UtilFormTypes.DateRange[] | undefined {
        const startsString = params.get(starts);
        const endsString = params.get(ends);
        if (startsString == null || endsString == null) {
          return undefined;
        }
        // Decoding runs during render, so a malformed link must fall back to defaults rather than throw.
        try {
          const startValues = decodeURIComponent(startsString).split(",");
          const endValues = decodeURIComponent(endsString).split(",");
          return startValues
            .map((startValue, index) => {
              const endValue = endValues[index];
              return {
                start: startValue ? DateHelper.resetTime(new Date(startValue)) : undefined,
                end: endValue ? DateHelper.resetTime(DateHelper.getLastDayOfMonth(new Date(endValue))) : undefined,
              };
            })
            .filter((range) => DateHelper.isValidDate(range.start) && DateHelper.isValidDate(range.end));
        } catch {
          return undefined;
        }
      },
    };
  },
};

export default QueryStateHelper;
